// Nhập đơn từ Excel / CSV với cột tự do: khớp tiêu đề cột của file khách vào các trường của ShopTrack
// (theo tên quen thuộc + AI đọc cột ở apps/api), rồi gom các dòng thành đơn nhiều mặt hàng.
// Hàm thuần, dùng chung web (đọc file) và api (prompt AI, kiểm kết quả AI). Test: import-columns.test.ts.

import { toBankingUnaccent } from './banking.js'

/** Trường ShopTrack cần khi nhập đơn. `hint` dùng trong prompt AI. */
export const IMPORT_FIELDS = {
  orderCode: { label: 'Mã đơn', hint: 'mã đơn hàng của thành viên / số thứ tự đơn' },
  customerName: { label: 'Tên khách hàng', hint: 'tên người nhận' },
  customerPhone: { label: 'Số điện thoại', hint: 'SĐT người nhận' },
  address: { label: 'Địa chỉ giao hàng', hint: 'địa chỉ chi tiết (số nhà, đường…)' },
  city: { label: 'Tỉnh/Thành phố', hint: 'tỉnh / thành phố nhận' },
  district: { label: 'Quận/Huyện', hint: 'quận / huyện nhận' },
  ward: { label: 'Phường/Xã', hint: 'phường / xã nhận' },
  productName: { label: 'Tên sản phẩm', hint: 'tên hàng hoá' },
  sku: { label: 'Mã SKU', hint: 'mã sản phẩm / SKU' },
  qty: { label: 'Số lượng', hint: 'số lượng sản phẩm của dòng' },
  weight: { label: 'Khối lượng', hint: 'cân nặng của dòng (gram hoặc kg)' },
  cod: { label: 'Tiền thu hộ COD', hint: 'tiền thu hộ của người nhận' },
  price: { label: 'Đơn giá bán', hint: 'giá bán một sản phẩm' },
  cost: { label: 'Giá vốn', hint: 'giá vốn / tiền hàng' },
  declaredValue: { label: 'Khai giá bảo hiểm', hint: 'giá trị khai giá / bảo hiểm hàng hoá' },
  shipmentPayer: { label: 'Người trả cước', hint: '0 = shop trả, 1 = khách trả' },
  pickupType: { label: 'Hình thức lấy', hint: '1 = gửi tại bưu cục, 2 = shipper đến lấy' },
  lengthCm: { label: 'Dài (cm)', hint: 'chiều dài kiện (cm)' },
  widthCm: { label: 'Rộng (cm)', hint: 'chiều rộng kiện (cm)' },
  heightCm: { label: 'Cao (cm)', hint: 'chiều cao kiện (cm)' },
  channel: { label: 'Kênh bán', hint: 'kênh bán hàng (Facebook, Shopee…)' },
  warehouse: { label: 'Kho', hint: 'tên kho lấy hàng' },
  note: { label: 'Ghi chú', hint: 'ghi chú giao hàng' },
  journeyType: { label: 'Loại đơn', hint: '1 = giao, 2 = thu hồi, 3 = đổi' },
} as const

export type ImportField = keyof typeof IMPORT_FIELDS
export const IMPORT_FIELD_KEYS = Object.keys(IMPORT_FIELDS) as ImportField[]

/** Trường → tên cột trong file (null = file không có). */
export type ColumnMapping = Partial<Record<ImportField, string>>

export interface ColumnPlan {
  mapping: ColumnMapping
  /** Đơn vị cột khối lượng */
  weightUnit: 'g' | 'kg'
}

/** "  Khai giá  bảo hiểm " → "khai gia bao hiem" — so tên cột không phân biệt dấu / hoa / khoảng trắng. */
export function normalizeHeader(h: string): string {
  return toBankingUnaccent(String(h ?? '')).toLowerCase().trim()
}

/** Tên cột quen thuộc (đã chuẩn hoá) cho từng trường — gồm mẫu ShopTrack cũ và tên tiếng Anh. */
const ALIASES: Record<ImportField, string[]> = {
  orderCode: ['ma don', 'ma don hang', 'code', 'id', 'order id', 'order code', 'stt don'],
  customerName: ['ten khach hang', 'khach hang', 'customer name', 'name', 'ten nguoi nhan', 'nguoi nhan'],
  customerPhone: ['so dien thoai', 'sdt', 'phone', 'dien thoai', 'sdt nguoi nhan'],
  address: ['dia chi giao hang', 'dia chi', 'address', 'dia chi nhan'],
  city: ['tinh thanh pho', 'tinh thanh', 'tinh', 'city', 'thanh pho'],
  district: ['quan huyen', 'huyen', 'quan', 'district'],
  ward: ['phuong xa', 'xa', 'phuong', 'ward'],
  productName: ['ten san pham', 'san pham', 'product', 'ten hang', 'hang hoa', 'product name'],
  sku: ['ma sku', 'sku', 'ma san pham', 'ma hang'],
  qty: ['so luong', 'qty', 'quantity', 'sl'],
  weight: ['khoi luong gram', 'khoi luong', 'can nang', 'weight', 'trong luong', 'khoi luong g', 'khoi luong kg', 'can nang kg', 'can nang gram'],
  cod: ['tien thu ho cod', 'cod', 'thu ho', 'tien thu ho'],
  price: ['don gia', 'gia ban', 'price', 'don gia ban'],
  cost: ['gia von', 'tien hang', 'cost'],
  declaredValue: ['khai gia bao hiem', 'gia tri khai gia bao hiem', 'khai gia', 'bao hiem', 'productprice'],
  shipmentPayer: ['nguoi tra cuoc 0 shop 1 khach', 'nguoi tra cuoc', 'shipmentpayer'],
  pickupType: ['hinh thuc lay 1 buu cuc 2 shipper', 'hinh thuc lay', 'pickuptype'],
  lengthCm: ['dai cm', 'dai', 'length'],
  widthCm: ['rong cm', 'rong', 'width'],
  heightCm: ['cao cm', 'cao', 'height'],
  channel: ['kenh ban', 'kenh', 'channel'],
  warehouse: ['kho', 'kho lay hang'],
  note: ['ghi chu', 'note'],
  journeyType: ['loai don 1 giao 2 thu 3 doi', 'loai don', 'journeytype'],
}

/** Khớp theo tên quen thuộc — mỗi cột chỉ gán cho một trường. */
export function aliasMapping(headers: readonly string[]): ColumnMapping {
  const out: ColumnMapping = {}
  const used = new Set<string>()
  const norm = headers.map((h) => [h, normalizeHeader(h)] as const)
  for (const field of IMPORT_FIELD_KEYS) {
    const hit = norm.find(([h, n]) => !used.has(h) && ALIASES[field].includes(n))
    if (hit) {
      out[field] = hit[0]
      used.add(hit[0])
    }
  }
  return out
}

/** Đơn vị khối lượng đoán từ tên cột ("Khối lượng (kg)" → kg). */
export function weightUnitOf(header: string | undefined): 'g' | 'kg' {
  return header && /\bkg\b/.test(normalizeHeader(header)) ? 'kg' : 'g'
}

/**
 * Kiểm kết quả AI: chỉ nhận trường có thật, tên cột có thật trong file, mỗi cột một trường.
 * Sai định dạng → {} (dùng khớp theo tên).
 */
export function sanitizeAiMapping(raw: unknown, headers: readonly string[]): { mapping: ColumnMapping; weightUnit: 'g' | 'kg' | null } {
  const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const src = obj.mapping && typeof obj.mapping === 'object' ? (obj.mapping as Record<string, unknown>) : obj
  const byNorm = new Map(headers.map((h) => [normalizeHeader(h), h]))
  const out: ColumnMapping = {}
  const used = new Set<string>()
  for (const field of IMPORT_FIELD_KEYS) {
    const v = src[field]
    if (typeof v !== 'string' || !v.trim()) continue
    const h = headers.includes(v) ? v : byNorm.get(normalizeHeader(v))
    if (h && !used.has(h)) {
      out[field] = h
      used.add(h)
    }
  }
  const wu = obj.weightUnit === 'kg' || obj.weightUnit === 'g' ? obj.weightUnit : null
  return { mapping: out, weightUnit: wu }
}

/**
 * Gộp: tên quen thuộc chắc chắn hơn nên ưu tiên; AI lấp các trường còn thiếu bằng cột chưa dùng.
 */
export function mergeMappings(alias: ColumnMapping, ai: ColumnMapping): ColumnMapping {
  const out: ColumnMapping = { ...alias }
  const used = new Set(Object.values(alias))
  for (const field of IMPORT_FIELD_KEYS) {
    const h = ai[field]
    if (!out[field] && h && !used.has(h)) {
      out[field] = h
      used.add(h)
    }
  }
  return out
}

/** Prompt cho AI: CHỈ tên cột (không gửi dữ liệu đơn). */
export function columnMappingPrompt(headers: readonly string[]): string {
  const fields = IMPORT_FIELD_KEYS.map((k) => `- ${k}: ${IMPORT_FIELDS[k].hint}`).join('\n')
  return [
    'Bạn ghép cột của một file Excel đơn hàng (tiếng Việt) vào các trường chuẩn.',
    'Các trường chuẩn:',
    fields,
    '',
    `Tiêu đề cột trong file (giữ NGUYÊN chuỗi, kể cả dấu cách): ${JSON.stringify(headers)}`,
    '',
    'Trả về JSON đúng dạng {"mapping": {"<trường>": "<tiêu đề cột>"}, "weightUnit": "g" | "kg"}.',
    'Chỉ ghép khi chắc chắn; trường không có cột phù hợp thì bỏ qua. Mỗi cột chỉ dùng cho một trường.',
    'weightUnit: đơn vị của cột khối lượng theo tên cột (không rõ thì "g").',
  ].join('\n')
}

// ─── Đọc giá trị ô ──────────────────────────────────────────────────

/** Ô chữ: gộp mọi khoảng trắng / xuống dòng thành một dấu cách. */
export function cellText(v: unknown): string {
  return String(v ?? '').replace(/\s+/g, ' ').trim()
}

/** Ô số: "1.000.000", "1,000,000 đ", "12 kg" → số; trống / không đọc được → null. */
export function cellNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v ?? '').trim()
  if (!s) return null
  const digits = s.replace(/[^\d.,-]/g, '')
  if (!digits) return null
  // Dấu . hoặc , đứng trước đúng 3 chữ số = phân cách nghìn; còn lại là thập phân
  const normalized = /^-?\d{1,3}([.,]\d{3})+$/.test(digits) ? digits.replace(/[.,]/g, '') : digits.replace(',', '.')
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

// ─── Dòng file → đơn ────────────────────────────────────────────────

export interface ImportLine {
  rowNum: number
  orderCode: string
  customerName: string
  customerPhone: string
  address: string
  city: string
  district: string
  ward: string
}

export interface ImportItemLine {
  name: string
  sku: string
  qty: number
  /** Cân ghi ở dòng (gram) — null = trống */
  weightGram: number | null
  cost: number
  price: number | null
  /** Khai giá ghi ở dòng — null = trống */
  declaredValue?: number | null
}

/**
 * Giá trị ghi lặp ở mọi dòng (file tách mỗi cái một dòng chép lại cùng một số) → tính một lần;
 * khác nhau → cộng. Bỏ ô trống.
 */
export function repeatedOrSum(values: ReadonlyArray<number | null | undefined>): number {
  const vs = values.filter((v): v is number => v != null && Number.isFinite(v))
  if (!vs.length) return 0
  return vs.length > 1 && vs.length === values.length && vs.every((v) => v === vs[0]) ? vs[0]! : vs.reduce((a, v) => a + v, 0)
}

export interface MergedItem {
  name: string
  sku: string
  qty: number
  /** Cân MỖI cái (gram) */
  unitWeightGram: number
  /** Giá vốn MỖI cái */
  unitCost: number
  unitPrice: number | null
}

/**
 * Mặt hàng + cân của một đơn đã gom.
 *  · Cùng sản phẩm (SKU, hoặc tên không phân biệt hoa thường) ở nhiều dòng → một mặt hàng, cộng số lượng
 *    (file kiểu "mỗi dòng một cái").
 *  · Cân mỗi dòng là cân CỦA DÒNG đúng như file: cộng lại theo sản phẩm; mỗi cái (để tạo sản phẩm) = cân
 *    dòng ÷ số lượng của dòng (dòng 20 cái, cân 20 kg → mỗi cái 1 kg). Dòng trống cân → `fallbackUnitGram`.
 *  · Cân đơn = tổng cân các sản phẩm; `parcels` = số sản phẩm (mỗi sản phẩm một kiện).
 */
export function mergeOrderItems(lines: readonly ImportItemLine[], fallbackUnitGram = 500): { items: MergedItem[]; weightGram: number; parcels: number; declaredValue: number } {
  // Gom theo sản phẩm trước — cân "kiện" tính theo TỪNG sản phẩm (file khách: mỗi sản phẩm một kiện,
  // tách mỗi cái một dòng thì cân kiện ghi lặp ở mọi dòng của sản phẩm đó)
  const byKey = new Map<string, ImportItemLine[]>()
  for (const l of lines) {
    const key = l.sku ? `s:${l.sku.toLowerCase()}` : `n:${l.name.toLowerCase()}`
    byKey.set(key, [...(byKey.get(key) ?? []), l])
  }
  let weightGram = 0
  let declaredValue = 0
  const items: MergedItem[] = []
  for (const group of byKey.values()) {
    // Cân / khai giá mỗi dòng giữ như file → cộng
    declaredValue += group.reduce((a, l) => a + (l.declaredValue ?? 0), 0)
    const qty = group.reduce((a, l) => a + l.qty, 0)
    const weight = group.reduce((a, l) => a + (l.weightGram != null && l.weightGram > 0 ? l.weightGram : fallbackUnitGram * l.qty), 0)
    weightGram += weight
    const first = group[0]!
    items.push({
      name: first.name,
      sku: first.sku,
      qty,
      unitWeightGram: Math.max(1, Math.round(weight / qty)),
      unitCost: Math.round(group.reduce((a, l) => a + l.cost, 0) / qty),
      unitPrice: group.find((l) => l.price != null)?.price ?? null,
    })
  }
  return { items, weightGram: Math.max(1, Math.round(weightGram)), parcels: items.length, declaredValue: Math.round(declaredValue) }
}

// ─── File → dòng → đơn ──────────────────────────────────────────────

/** Một dòng file đã đọc theo bảng ghép cột (chưa kiểm hợp lệ). */
export interface ParsedImportLine extends ImportLine, ImportItemLine {
  cod: number | null
  shipmentPayer: 0 | 1
  pickupType: 1 | 2
  lengthMm: number | null
  widthMm: number | null
  heightMm: number | null
  channel: string
  warehouse: string
  note: string
  journeyType: string
  /** Ô số không đọc được (để báo lỗi): tên trường */
  badNumbers: string[]
}

/** Đọc một dòng theo bảng ghép cột. Khối lượng quy về gram; kích thước cm → mm. */
export function parseImportLine(row: Record<string, unknown>, rowNum: number, plan: ColumnPlan): ParsedImportLine {
  // Thư viện đọc Excel có chỗ cắt dấu cách ở tiêu đề, có chỗ giữ nguyên (" Khai giá bảo hiểm ") —
  // không khớp chính xác thì tìm theo tên đã chuẩn hoá
  const raw = (f: ImportField) => {
    const h = plan.mapping[f]
    if (!h) return ''
    if (h in row) return row[h]
    const n = normalizeHeader(h)
    const k = Object.keys(row).find((key) => normalizeHeader(key) === n)
    return k ? row[k] : ''
  }
  const text = (f: ImportField) => cellText(raw(f))
  const badNumbers: string[] = []
  const num = (f: ImportField) => {
    const v = raw(f)
    const n = cellNumber(v)
    if (n == null && cellText(v)) badNumbers.push(IMPORT_FIELDS[f].label)
    return n
  }
  const qty = num('qty')
  const weight = num('weight')
  const dim = (f: ImportField) => {
    const n = num(f)
    return n != null && n > 0 ? Math.round(n * 10) : null
  }
  return {
    rowNum,
    orderCode: text('orderCode'),
    customerName: text('customerName'),
    customerPhone: text('customerPhone'),
    address: text('address'),
    city: text('city'),
    district: text('district'),
    ward: text('ward'),
    name: text('productName') || text('sku'),
    sku: text('sku'),
    qty: qty != null && qty >= 1 ? Math.round(qty) : 1,
    weightGram: weight != null && weight > 0 ? Math.round(plan.weightUnit === 'kg' ? weight * 1000 : weight) : null,
    cost: Math.max(0, Math.round(num('cost') ?? 0)),
    price: (() => {
      const p = num('price')
      return p != null && p >= 0 ? Math.round(p) : null
    })(),
    declaredValue: (() => {
      const d = num('declaredValue')
      return d != null && d >= 0 ? Math.round(d) : null
    })(),
    cod: (() => {
      const c = num('cod')
      return c != null ? Math.round(c) : null
    })(),
    shipmentPayer: cellNumber(raw('shipmentPayer')) === 1 ? 1 : 0,
    pickupType: cellNumber(raw('pickupType')) === 1 ? 1 : 2,
    lengthMm: dim('lengthCm'),
    widthMm: dim('widthCm'),
    heightMm: dim('heightCm'),
    channel: text('channel'),
    warehouse: text('warehouse'),
    note: text('note'),
    journeyType: text('journeyType'),
    badNumbers,
  }
}

export interface ImportOrderDraft {
  line: ParsedImportLine
  items: MergedItem[]
  qty: number
  weightGram: number
  cod: number
  cost: number
  declaredValue: number
  note: string
  /** Kích thước kiện ghi ở dòng (đủ Dài / Rộng / Cao) */
  dims: { lengthMm: number; widthMm: number; heightMm: number } | null
  /** Dòng khác trong file ghi cùng mã đơn — không tạo được hai đơn cùng mã */
  sameCodeRows: number[]
}

/**
 * Mỗi dòng file là MỘT đơn (01/11/2026 bỏ gom nhiều dòng — sản phẩm gộp vào một ô). Cân / khai giá / COD /
 * kích thước giữ đúng như dòng: 12000 g là 12 kg, không đoán "kiện ghi lặp" (16/10/2026 đoán vậy làm
 * 1665 đơn 12 kg thành 0,6–1,2 kg). Dòng trùng mã đơn → `sameCodeRows`: file kiểu cũ nhiều dòng một đơn
 * phải báo, không lặng lẽ thành nhiều vận đơn.
 */
export function buildImportOrders(lines: readonly ParsedImportLine[], fallbackUnitGram = 500): ImportOrderDraft[] {
  const rowsByCode = new Map<string, number[]>()
  for (const l of lines) if (l.orderCode) rowsByCode.set(l.orderCode, [...(rowsByCode.get(l.orderCode) ?? []), l.rowNum])
  return lines.map((line) => {
    const merged = mergeOrderItems([{ ...line, name: line.name || 'Hàng hóa' }], fallbackUnitGram)
    return {
      line,
      items: merged.items,
      qty: line.qty,
      weightGram: merged.weightGram,
      cod: Math.max(0, line.cod ?? 0),
      cost: line.cost,
      declaredValue: merged.declaredValue,
      note: line.note.slice(0, 300),
      dims: line.lengthMm && line.widthMm && line.heightMm ? { lengthMm: line.lengthMm, widthMm: line.widthMm, heightMm: line.heightMm } : null,
      sameCodeRows: (line.orderCode ? rowsByCode.get(line.orderCode)! : []).filter((n) => n !== line.rowNum),
    }
  })
}

/** Tên hàng gửi BEST cho đơn nhiều món: "A (x20), B (x10)" ≤ 200 ký tự (như form tạo đơn). */
export function packageName(items: ReadonlyArray<{ name: string; qty: number }>): string {
  return items.map((i) => `${i.name}${i.qty > 1 ? ` (x${i.qty})` : ''}`).join(', ').slice(0, 200)
}
